import {
  SkuCodeRenderCell,
  ProposedProductionAmountRenderCell,
  TemplateWeekRenderCell,
  ExcessDaysProjectedRenderCell,
} from './Column.definition.support.methods'
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColumnGroupingModel,
} from '@mui/x-data-grid-premium'
import { PanelSiteDataType, SkuDataType } from './EditProduction.types'
import { searchTextOperator } from 'Common/MUITable/SearchText.operator'

const multiselectOptions = (
  data: Record<string, string | number | boolean>[]
) => {
  const skuCodeSet = new Set<string>()
  const workCentreCodeSet = new Set<string>()
  data?.forEach((column) => {
    skuCodeSet.add(column.skuCode as string)
    workCentreCodeSet.add(column.workCentreCode as string)
  })
  return {
    skuCodes: Array.from(skuCodeSet),
    workCentreCodes: Array.from(workCentreCodeSet),
  }
}

const specialSearch = (arr: string[], label: string) => {
  return {
    ...searchTextOperator[0],
    InputComponentProps: { data: arr, label },
  }
}

export const editProductionColumns = (
  weeks: string[],
  data: Record<string, unknown>[]
) => {
  const { skuCodes, workCentreCodes } = multiselectOptions(data)
  const baseData = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    },
    {
      field: 'skuCode',
      headerName: 'SKU Code',
      width: 100,
      renderCell: SkuCodeRenderCell,
      type: 'singleSelect',
      valueOptions: skuCodes,
      filterOperators: [specialSearch(skuCodes, 'SKU Code')],
    },
    {
      field: 'workCentreCode',
      headerName: 'Work Centre Code',
      width: 100,
      type: 'singleSelect',
      valueOptions: workCentreCodes,
      filterOperators: [specialSearch(workCentreCodes, 'Work Centre Code')],
    },

    {
      field: 'runRate',
      headerName: 'Run rate',
      width: 100,
    },
    {
      field: `minimumProductionOrderIncrement`,
      headerName: `MOI`,
      width: 100,
    },
    {
      field: `minimumProductionOrderQuantity`,
      headerName: `MOQ`,
      width: 100,
    },
    {
      field: `replenishmentTypeCode`,
      headerName: `Replenishment type`,
      width: 100,
    },
    {
      field: `cycleTime`,
      headerName: `Cycle time`,
      width: 100,
    },
    {
      field: `batchGroupCode`,
      headerName: `Batch group code`,
      with: 100,
    },
    {
      field: `skuDescription`,
      headerName: `SKU description`,
      width: 100,
    },
  ]
  const dynamicColumns = weeks.map((week: string) => {
    return [
      {
        field: `${week}_proposedProductionAmount`,
        headerName: `Production amount`,
        width: 150,
        editable: true,
        type: 'number',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.proposedProductionAmount ?? 0,
        renderCell: (props) => {
          const params = { ...props, week }
          return ProposedProductionAmountRenderCell(params)
        },
      },
      {
        field: `${week}_adjustedProductionHours`,
        headerName: `Production time`,
        width: 150,
        cellClassName: 'calculated',
        type: 'number',
        editable: true,
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.adjustedProductionHours ?? 0,
      },
      {
        field: `${week}_templateWeek`,
        headerName: `Template week`,
        width: 150,
        type: 'boolean',
        aggregable: false,
        valueGetter: (_, row: SkuDataType) => row[week]?.templateWeek ?? 0,
        renderCell: (props) => {
          const params = { ...props, week }
          return TemplateWeekRenderCell(params)
        },
      },
      {
        field: `${week}_weekCloseDaysProjected`,
        headerName: `Week close days projected`,
        width: 150,
        cellClassName: 'calculated',
        type: 'number',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.weekCloseDaysProjected ?? 0,
      },
      {
        field: `${week}_excessDaysProjected`,
        headerName: `Excess stock in days`,
        width: 150,
        cellClassName: 'calculated',
        type: 'number',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.excessDaysProjected ?? 0,
        renderCell: (props) => {
          const params = { ...props, week }
          return ExcessDaysProjectedRenderCell(params)
        },
      },
      {
        field: `${week}_alternativeWCProductionAmount`,
        headerName: `Alternative WC production amount`,
        width: 150,
        type: 'number',
        editable: false,
        aggregable: false,
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.alternativeWCProductionAmount ?? 0,
      },
      {
        field: `${week}_changeInProductionAmount`,
        headerName: `Change in production amount`,
        width: 150,
        cellClassName: 'calculated',
        type: 'number',
        aggregable: false,
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.changeInProductionAmount ?? 0,
      },

      {
        field: `${week}_changeInProductionHours`,
        headerName: `Change in production hours`,
        width: 150,
        cellClassName: 'calculated',
        type: 'number',
        aggregable: false,
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.changeInProductionHours ?? 0,
      },
      {
        field: `${week}_cumulativeChangeInProduction`,
        headerName: `Cumulative change in production`,
        width: 150,
        type: 'number',
        aggregable: false,
        cellClassName: 'cumulative-calculated',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.cumulativeChangeInProduction ?? 0,
      },

      {
        field: `${week}_initialProductionAmount`,
        headerName: `Initial production amount`,
        width: 150,
        type: 'number',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.initialProductionAmount ?? 0,
      },
      {
        field: `${week}_initialProductionHours`,
        headerName: `Initial production hours`,
        width: 150,
        type: 'number',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.initialProductionHours ?? 0,
      },
      {
        field: `${week}_inventoryExcessProjected`,
        headerName: `Inventory excess projected`,
        width: 150,
        type: 'number',
        cellClassName: 'calculated',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.inventoryExcessProjected ?? 0,
      },
      {
        field: `${week}_salesRate`,
        headerName: `Sales rate`,
        width: 150,
        type: 'number',
        valueGetter: (_, row: SkuDataType) => row[week]?.salesRate ?? 0,
      },
      {
        field: `${week}_siteWithSalesCount`,
        headerName: `Site with sales count`,
        width: 150,
        type: 'number',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.siteWithSalesCount ?? 0,
      },
      {
        field: `${week}_targetCloseDays`,
        headerName: `Target close days`,
        width: 150,
        type: 'number',
        valueGetter: (_, row: SkuDataType) => row[week]?.targetCloseDays ?? 0,
      },
      {
        field: `${week}_targetCloseInventory`,
        headerName: `Target close inventory`,
        width: 150,
        type: 'number',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.targetCloseInventory ?? 0,
      },
      {
        field: `${week}_templateViolation`,
        headerName: `Template violation`,
        width: 150,
        type: 'number',
        valueGetter: (_, row: SkuDataType) => row[week]?.templateViolation ?? 0,
      },

      {
        field: `${week}_totalProductionAmount`,
        headerName: `Total production amount`,
        width: 150,
        type: 'number',
        cellClassName: 'calculated',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.totalProductionAmount ?? 0,
      },
      {
        field: `${week}_weekCloseInventoryInitial`,
        headerName: `Week close inventory initial`,
        width: 150,
        type: 'number',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.weekCloseInventoryInitial ?? 0,
      },
      {
        field: `${week}_weekCloseInventoryProjected`,
        headerName: `Week close inventory projected`,
        width: 150,
        type: 'number',
        cellClassName: 'calculated',
        valueGetter: (_, row: SkuDataType) =>
          row[week]?.weekCloseInventoryProjected ?? 0,
      },
    ]
  })
  return [...baseData, ...dynamicColumns.flat()]
}

export const columnGroupingModel = (weeks: string[]) => {
  const columnGrouping = [
    {
      groupId: 'INFO',
      children: [
        { field: 'workCentreCode' },
        { field: 'runRate' },
        { field: 'minimumProductionOrderIncrement' },
        { field: 'minimumProductionOrderQuantity' },
        { field: 'replenishmentTypeCode' },
        { field: 'cycleTime' },
        { field: 'batchGroupCode' },
        { field: 'skuDescription' },
      ],
    },
  ]
  const dynamicColumnGrouping = weeks.map((week) => {
    return {
      groupId: `${week}`,
      children: [
        { field: `${week}_adjustedProductionHours` },
        { field: `${week}_alternativeWCProductionAmount` },
        { field: `${week}_changeInProductionAmount` },
        { field: `${week}_changeInProductionHours` },
        { field: `${week}_cumulativeChangeInProduction` },
        { field: `${week}_excessDaysProjected` },
        { field: `${week}_initialProductionAmount` },
        { field: `${week}_initialProductionHours` },
        { field: `${week}_inventoryExcessProjected` },
        { field: `${week}_minimumProductionOrderIncrement` },
        { field: `${week}_minimumProductionOrderQuantity` },
        { field: `${week}_proposedProductionAmount` },
        { field: `${week}_salesRate` },
        { field: `${week}_siteWithSalesCount` },
        { field: `${week}_targetCloseDays` },
        { field: `${week}_targetCloseInventory` },
        { field: `${week}_templateViolation` },
        { field: `${week}_templateWeek` },
        { field: `${week}_totalProductionAmount` },
        { field: `${week}_weekCloseDaysProjected` },
        { field: `${week}_weekCloseInventoryInitial` },
        { field: `${week}_weekCloseInventoryProjected` },
      ],
    }
  })
  return [...columnGrouping, ...dynamicColumnGrouping]
}

export const columnVisibilityModel = (
  weeks: string[],
  columns: Record<string, unknown>[]
) => {
  const defaultVisibleColumns = weeks
    .map((week) => [
      `${week}_adjustedProductionHours`,
      `${week}_excessDaysProjected`,
      `${week}_proposedProductionAmount`,
      `${week}_weekCloseDaysProjected`,
      `${week}_templateWeek`,
      `skuCode`,
      `workCentreCode`,
      `runRate`,
      `minimumProductionOrderIncrement`,
      `minimumProductionOrderQuantity`,
      `replenishmentTypeCode`,
      `cycleTime`,
    ])
    .flat()
  const obj = {}
  const allHiddenColumns = columns.filter((column) => {
    return !defaultVisibleColumns.includes(column.field as string)
  })

  allHiddenColumns.forEach((column) => {
    obj[column.field] = false
  })
  return obj
}

export const aggragationModelObj = (weeks: string[]) => {
  const aggregation: Record<string, string> = {}
  weeks.forEach((week) => {
    aggregation[`${week}_adjustedProductionHours`] = 'sum'
    aggregation[`${week}_proposedProductionAmount`] = 'renderCellAggregation'
    aggregation[`${week}_excessDaysProjected`] = 'renderCellNoAggregation'
    aggregation[`${week}_weekCloseDaysProjected`] = 'renderCellNoAggregation'
    aggregation[`${week}_templateWeek`] = 'renderCellNoAggregation'
    aggregation[`${week}_changeInProductionAmount`] = 'renderCellNoAggregation'
    aggregation[`${week}_changeInProductionHours`] = 'renderCellNoAggregation'
    aggregation[`${week}_cumulativeChangeInProduction`] =
      'renderCellNoAggregation'
    aggregation[`${week}_alternativeWCProductionAmount`] =
      'renderCellNoAggregation'
    aggregation[`${week}_initialProductionAmount`] = 'renderCellNoAggregation'
    aggregation[`${week}_initialProductionHours`] = 'renderCellNoAggregation'
    aggregation[`${week}_inventoryExcessProjected`] = 'renderCellNoAggregation'
    aggregation[`${week}_salesRate`] = 'renderCellNoAggregation'
    aggregation[`${week}_siteWithSalesCount`] = 'renderCellNoAggregation'
    aggregation[`${week}_targetCloseDays`] = 'renderCellNoAggregation'
    aggregation[`${week}_targetCloseInventory`] = 'renderCellNoAggregation'
    aggregation[`${week}_templateViolation`] = 'renderCellNoAggregation'
    aggregation[`${week}_totalProductionAmount`] = 'renderCellNoAggregation'
    aggregation[`${week}_weekCloseInventoryInitial`] = 'renderCellNoAggregation'
    aggregation[`${week}_weekCloseInventoryProjected`] =
      'renderCellNoAggregation'
  })
  return aggregation
}

export const panelContentTableColumns = (weeks: string[]) => {
  const statisColumns = [
    {
      field: 'siteCode',
      headerName: 'Site Code',
      width: 150,
    },
    {
      field: 'replenishmentMinimumOrderIncrement',
      headerName: 'MOI',
      width: 150,
    },
    {
      field: 'replenishmentMinimumOrderQuantity',
      headerName: 'MOQ',
      width: 150,
    },
  ]
  const dynamicColumns = weeks
    .map((week: string) => {
      return [
        {
          field: `${week}_proposedArrivalAmount`,
          headerName: 'Proposed arrival amount',
          editable: true,
          valueGetter: (_, row: PanelSiteDataType) => {
            return row[`${week}`]?.proposedArrivalAmount ?? '-'
          },
          flex: 1,
        },
        {
          field: `${week}_formattedArrivalDateKey`,
          headerName: 'Formatted arrival date key',
          valueGetter: (_, row: PanelSiteDataType) => {
            return row[`${week}`]?.formattedArrivalDateKey ?? '-'
          },
          flex: 1,
        },
        {
          field: `${week}_projectedClosingDays`,
          headerName: 'Projected closing days',
          valueGetter: (_, row: PanelSiteDataType) => {
            return row[`${week}`]?.projectedClosingDays ?? '-'
          },
          flex: 1,
        },
        {
          field: `${week}_inventoryExcessDays`,
          headerName: 'Inventory excess days',
          valueGetter: (_, row: PanelSiteDataType) => {
            return row[`${week}`]?.inventoryExcessDays ?? '-'
          },
          flex: 1,
        },
      ]
    })
    .flat()
  return [...statisColumns, ...dynamicColumns]
}

export const panelTableColumnGrouping = (weeks: string[]) => {
  const panelDataGroupingModel: GridColumnGroupingModel = [
    {
      groupId: 'INFO',
      children: [
        { field: 'siteCode' },
        { field: 'replenishmentMinimumOrderIncrement' },
        { field: 'replenishmentMinimumOrderQuantity' },
      ],
    },
  ]

  const panelDataGroupingModelDynamic = weeks.map((week: string) => {
    return {
      groupId: `${week}`,
      children: [
        { field: `${week}_changeInArrivalAmount` },
        { field: `${week}_cumulativeChangeInProduction` },
        { field: `${week}_formattedArrivalDateKey` },
        { field: `${week}_formattedProductionDate` },
        { field: `${week}_initialArrivalAmount` },
        { field: `${week}_initialClosingInventory` },
        { field: `${week}_inventoryExcess` },
        { field: `${week}_inventoryExcessDays` },
        { field: `${week}_projectedClosingDays` },
        { field: `${week}_projectedClosingInventory` },
        { field: `${week}_projectedLowStock` },
        { field: `${week}_projectedStockOut` },
        { field: `${week}_proposedArrivalAmount` },
        { field: `${week}_salesRate` },
        { field: `${week}_targetCloseDays` },
        { field: `${week}_targetCloseInventory` },
        { field: `${week}_templateWeek` },
      ],
    }
  })

  return [...panelDataGroupingModel, ...panelDataGroupingModelDynamic]
}
